import { type ButtonHTMLAttributes, type ReactNode, type Ref } from 'react';
import clsx from 'clsx';
import './ChoiceButton.css';

export type ChoiceState = 'idle' | 'correct' | 'wrong';

export interface ChoiceButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Answer text. */
  children: ReactNode;
  /** Shortcut label drawn in the leading bullet (e.g. "A", "1"). */
  bullet?: ReactNode;
  /** Whether the user has this one picked (pre-check). */
  selected?: boolean;
  /** Post-check reveal state. */
  state?: ChoiceState;
  ref?: Ref<HTMLButtonElement>;
}

/**
 * ChoiceButton — tall selectable card used in multiple-choice exercises.
 * States: idle (default/hover/selected) and post-check (correct / wrong).
 */
export function ChoiceButton({
  children,
  bullet,
  selected,
  state = 'idle',
  type,
  className,
  disabled,
  ref,
  ...rest
}: ChoiceButtonProps) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      disabled={disabled}
      aria-pressed={selected || undefined}
      data-selected={selected || undefined}
      data-state={state === 'idle' ? undefined : state}
      className={clsx('fluent-choicebtn', className)}
      {...rest}
    >
      {bullet != null && (
        <span className="fluent-choicebtn__bullet" aria-hidden="true">
          {bullet}
        </span>
      )}
      <span className="fluent-choicebtn__label">{children}</span>
    </button>
  );
}
